import type { ComponentType } from "react";
import { createBrowserRouter, createPath, Navigate } from "react-router-dom";
import { AppShell } from "@/shell/AppShell";
import { NAV } from "@/shell/nav";
import { CandidateRedirect } from "@/pages/Candidates/candidateLink";
import { RouteError } from "@/components/ChunkFailed";
import { onChunkError } from "@/lib/appUpdate";

// Каждая страница — отдельный кусок сборки. Раньше был один файл на 1,1 МБ
// (330 КБ gzip): чаты, карточка кандидата, JsSIP и все разделы грузились и
// разбирались до первого экрана любой страницы.
//
// `lazy` роутера, а не React.lazy. Кусок начинает грузиться, как только роутер
// создан — при загрузке этого модуля, параллельно с /api/me, а не после входа.
// При переходе прежняя страница остаётся на экране, пока новая не догрузится:
// без мигания заглушки и без прыжка вёрстки. Первый экран ждёт куска под той
// же «Загрузка…», что и вход (fallbackElement в main.tsx).
//
// errorElement — только для недогруженного куска (RouteError, lib/appUpdate);
// любую другую ошибку он отдаёт дальше, к стандартному экрану роутера.
function lazyPage(load: () => Promise<ComponentType>) {
  return {
    lazy: () =>
      load().then(
        (Component) => ({ Component }),
        // Со страницы, с которой уходят, терять нечего: набранное и открытые
        // окна перезагрузку не держат (ждёт она только звонка, отправки и
        // загрузки), и ведёт она туда, куда пользователь шёл. На первом экране
        // роутера ещё нет (lazy зовётся из самого createBrowserRouter), но и
        // адрес там уже тот, что нужен.
        (err: unknown) => {
          const to = created?.state.navigation.location;
          return onChunkError(err, { leaving: true, to: to ? createPath(to) : undefined });
        },
      ),
    errorElement: <RouteError />,
  };
}

// Сам роутер — ради адреса перехода в lazyPage; до конца createBrowserRouter
// обращаться к `router` нельзя.
let created: typeof router | null = null;

// One route per ready nav entry.
const pageRoutes = NAV.flatMap((section) => section.items)
  .filter((item) => item.ready && item.page)
  .map((item) => ({
    path: item.path.replace(/^\//, ""),
    ...lazyPage(item.page),
    // Вакансия — окно поверх списка (как карточка кандидата): маршрут вложен,
    // список остаётся смонтированным под ней и сам рисует <Outlet />.
    ...(item.path === "/vacancies"
      ? {
          children: [
            {
              path: ":id",
              ...lazyPage(() =>
                import("@/pages/Vacancies/VacancyDetailPage").then((m) => m.VacancyDetailPage),
              ),
            },
          ],
        }
      : {}),
    // Карточка человека — тоже окно поверх списка «Люди»: маршрут вложен, список
    // остаётся под ней (PeoplePage рисует <Outlet />).
    ...(item.path === "/people"
      ? {
          children: [
            {
              path: ":id",
              ...lazyPage(() =>
                import("@/pages/People/PersonCardPage").then((m) => m.PersonCardPage),
              ),
            },
          ],
        }
      : {}),
  }));

export const router = createBrowserRouter([
  {
    path: "/",
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to="/vacancies" replace /> },
      ...pageRoutes,
      { path: "candidates/:id", element: <CandidateRedirect /> },
      // «Шаблоны» стали вкладкой «Настроек»; старые закладки и ссылки ведут туда.
      { path: "templates", element: <Navigate to="/settings?tab=templates" replace /> },
      // Выдача поиска под вакансию; `ref` — номер вакансии или её uuid. Выбор
      // вакансии (/search) — та же страница, её маршрут — из NAV.
      {
        path: "search/:ref",
        ...lazyPage(() => import("@/pages/Search/SearchPage").then((m) => m.SearchPage)),
      },
      { path: "*", element: <Navigate to="/vacancies" replace /> },
    ],
  },
  // Обёртка без пути и без элемента — только ради стандартного экрана ошибки
  // роутера. RouteError отдаёт наверх всё, что не «кусок не догрузился», а у
  // маршрута верхнего уровня «наверху» уже нет никого: ошибка в коде мобильной
  // версии размонтировала бы всё приложение (белый экран без плашки
  // обновления) вместо прежнего «Unexpected Application Error!». Страницам
  // шелла такая не нужна — над ними маршрут «/».
  {
    children: [
      // Framed by the browser extension's side panel: no AppShell (sidebar), but
      // still behind the auth Gate in main.tsx.
      {
        path: "/embed/person-panel",
        ...lazyPage(() => import("@/pages/Chats/PersonPanelEmbed").then((m) => m.PersonPanelEmbed)),
      },
      // Телефон: вкладки — пути под /m/ (mobile/tabs), экраны поверх них — в
      // параметрах адреса. Незнакомый путь под /m MobileApp сам ведёт на `/m/`.
      // Мобильная версия (PWA: чаты, кандидаты, интервью) — отдельным куском:
      // полной версии её код и стили не нужны. Не-телефон её не рисует
      // (main.tsx), хотя роутер и начнёт грузить кусок, открой компьютер /m/.
      {
        path: "/m/*",
        ...lazyPage(() => import("@/mobile/MobileApp").then((m) => m.default)),
      },
      // Окно программы «HRDeva» для Windows (tray/bridge.ts): только
      // входящие звонки, без шелла. В обычном браузере — ссылка на установщик.
      {
        path: "/tray",
        ...lazyPage(() => import("@/tray/TrayApp").then((m) => m.default)),
      },
      // Окно «Транскрибировать файл…» того же приложения: свой кусок сборки —
      // окну звонков его код не нужен, а открывают это окно раз в день.
      {
        path: "/tray/transcribe",
        ...lazyPage(() => import("@/tray/TranscribePage").then((m) => m.default)),
      },
      // Окно «Инструменты»: та же расшифровка и работа с видео (склейка,
      // сжатие, обрезка) с переключателем. Его открывают новые версии
      // программы; старые по-прежнему ходят на /tray/transcribe.
      {
        path: "/tray/tools",
        ...lazyPage(() => import("@/tray/TrayToolsPage").then((m) => m.default)),
      },
      // Окно «Интервью»: экран «Интервью» без шелла, видеокабинет — в этом же
      // окне. Карточка кандидата и другие разделы оттуда уходят в браузер.
      {
        path: "/tray/interviews",
        ...lazyPage(() => import("@/tray/TrayInterviewsPage").then((m) => m.default)),
      },
      // Единое окно программы: «Звонки», «Интервью» и «Инструменты» с
      // переключателем в одной странице (tray/AppPage). Его открывают новые
      // версии программы; три окна выше — для установленных 1.0.5–1.0.7.
      {
        path: "/tray/app",
        ...lazyPage(() => import("@/tray/AppPage").then((m) => m.default)),
      },
    ],
  },
]);

created = router;
